import { useEffect, useState } from 'react';
import {
	ProbeFeatureFlagDefaults,
	ProbeMeetingFeatureFlags,
	ProbeResponse,
} from '../../connection/health';

export interface MeetingConfigResult {
	effective: ProbeResponse<Record<string, boolean>> | null; /** The room's effective flags, and the platform defaults to diff them against. */
	defaults: ProbeResponse<Record<string, boolean>> | null;
	isLoading: boolean;
}

/**
 * Fetches the room's feature flags once.
 *
 * Deliberately not part of the polling loop: flags are set before a show and do
 * not change during one, so re-fetching them every few seconds would add two
 * requests per tick for a value that never moves.
 */
export default function useMeetingConfig(meetingId: number | null): MeetingConfigResult {
	const [effective, setEffective] = useState<ProbeResponse<Record<string, boolean>> | null>(null);
	const [defaults, setDefaults] = useState<ProbeResponse<Record<string, boolean>> | null>(null);
	const [isLoading, setIsLoading] = useState(false);

	useEffect(() => {
		if (!meetingId) return undefined;

		let cancelled = false;
		setIsLoading(true);

		Promise.all([
			ProbeMeetingFeatureFlags(meetingId),
			ProbeFeatureFlagDefaults(),
		]).then(([meetingFlags, defaultFlags]) => {
			if (cancelled) return;
			setEffective(meetingFlags);
			setDefaults(defaultFlags);
			setIsLoading(false);
		});

		return () => { cancelled = true; };
	}, [meetingId]);

	return { effective, defaults, isLoading };
}
